/**
 * Achievements Panel Component
 * VOY-133: Badge collection and progression system
 */

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Trophy, Share2 } from 'lucide-react';

interface Achievement {
  id: string;
  name: string;
  description: string;
  icon: string;
  unlocked: boolean;
  progress?: {
    current: number;
    required: number;
  };
}

interface AchievementsPanelProps {
  achievements: Achievement[];
  totalUnlocked: number;
  totalAvailable: number;
  onShare: () => void;
}

export function AchievementsPanel({ 
  achievements, 
  totalUnlocked, 
  totalAvailable, 
  onShare 
}: AchievementsPanelProps) {
  return (
    <Card className="rounded-3xl bg-white/[0.08] dark:bg-zinc-800/50 backdrop-blur-xl border border-white/30 dark:border-zinc-700/50 shadow-[0_8px_24px_rgba(0,0,0,0.15)] dark:shadow-xl">
      <CardContent className="p-5">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-yellow-500" />
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">
              Your Achievements ({totalUnlocked}/{totalAvailable})
            </h3>
          </div>
          <Button
            onClick={onShare}
            variant="outline"
            size="sm"
            className="rounded-full dark:border-zinc-600 dark:text-zinc-300 dark:hover:bg-zinc-700"
          >
            <Share2 className="w-4 h-4" />
          </Button>
        </div>

        {/* Achievement Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
          {achievements.map((achievement) => (
            <div
              key={achievement.id}
              className={`p-3 rounded-3xl text-center transition-all backdrop-blur-xl border-2 shadow-[0_8px_24px_rgba(0,0,0,0.15)] dark:shadow-xl ${
                achievement.unlocked
                  ? 'bg-yellow-100/80 dark:bg-yellow-900/30 border-yellow-400 dark:border-yellow-600/50'
                  : 'bg-white/[0.08] dark:bg-zinc-700/50 border-white/30 dark:border-zinc-600/50 opacity-60'
              }`}
            >
              <div className="text-3xl mb-2">
                {achievement.unlocked ? achievement.icon : '🔒'}
              </div>
              <p className={`text-xs font-bold ${
                achievement.unlocked ? 'text-yellow-900 dark:text-yellow-300' : 'text-gray-600 dark:text-zinc-400'
              }`}>
                {achievement.name}
              </p>
              <p className={`text-xs mt-1 ${
                achievement.unlocked ? 'text-gray-700 dark:text-yellow-400/80' : 'text-gray-500 dark:text-zinc-500'
              }`}>
                {achievement.description}
              </p>
              {achievement.progress && !achievement.unlocked && (
                <div className="mt-2">
                  <div className="w-full bg-gray-300 dark:bg-zinc-600 rounded-full h-1.5">
                    <div
                      className="h-full bg-purple-500 rounded-full transition-all"
                      style={{ 
                        width: `${(achievement.progress.current / achievement.progress.required) * 100}%` 
                      }}
                    />
                  </div>
                  <p className="text-xs text-gray-500 dark:text-zinc-400 mt-1">
                    {achievement.progress.current}/{achievement.progress.required}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Next Achievement */}
        {achievements.find(a => !a.unlocked && a.progress) && (
          <div className="bg-white/[0.08] dark:bg-zinc-700/50 backdrop-blur-xl border border-white/30 dark:border-zinc-600/50 rounded-3xl p-3 shadow-[0_8px_24px_rgba(0,0,0,0.15)] dark:shadow-xl">
            <p className="text-sm font-semibold text-purple-900 dark:text-purple-400 mb-1">
              Next Unlock: {achievements.find(a => !a.unlocked && a.progress)?.name}
            </p>
            <p className="text-xs text-purple-700 dark:text-purple-400/80">
              {achievements.find(a => !a.unlocked && a.progress)?.progress?.current}/
              {achievements.find(a => !a.unlocked && a.progress)?.progress?.required} progress
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

